<template>
  <div class="welcome-page">
    <div class="full-card">
      <div class="welcome-header">
        <div class="header-content">
          <img
            v-if="logoFetched"
            :src="getLogo || defaultLogo"
            alt="Company Logo"
            class="hero-img"
            @error="onLogoError"
          />
        </div>
      </div>
      <div class="user-meta welcome-headername">
        <img
          :src="getAuthenticatedUser.avatar || defaultAvatar"
          alt="User Photo"
          class="user-photo"
        />
        <div>
          <h1>Welcome, {{ fullName }}!</h1>
        </div>
      </div>

      <div class="otp-section" v-if="loginType">
        <p class="setup-note text-center">
          <template v-if="loginType === 'OTP'">
            Enter the OTP to complete two-factor authentication.
            <br />
            The code was sent to
            <strong class="mfa-email">{{ getAuthenticatedUser.email }}</strong>
          </template>

          <template v-else-if="loginType === 'APP'">
            Enter the code from your authenticator app to complete two-factor authentication.
          </template>
        </p>

        <el-form @submit.native.prevent="verifyOTP">
          <el-form-item class="otp-input-wrapper">
            <el-input
              v-model="otp"
              maxlength="6"
              :placeholder="`Enter 6-digit ${
                loginType === 'OTP' ? 'OTP' : loginType === 'APP' ? 'app code' : 'code'
              }`"
              class="otp-input-large"
            />
          </el-form-item>

          <div class="otp-submit-wrapper">
            <el-button
              type="primary"
              :loading="verifying"
              @click="verifyOTP"
              class="otp-submit-btn"
              :disabled="submitDisabled"
            >
              Submit
            </el-button>
          </div>

          <div class="otp-resend-wrapper" v-if="loginType === 'OTP'">
            <el-button
              type="text"
              :disabled="resendDisabled"
              @click="handleSendOtp"
              class="otp-resend-btn"
            >
              Resend OTP
              <span v-if="resendCountdown">({{ resendCountdown }}s)</span>
            </el-button>
            <br />

            <a class="fw-medium" style="cursor: pointer; color: #2f54eb" @click="logoutUser()">
              Login with different account
            </a>
          </div>

          <div class="otp-switch-wrapper" v-if="canSwitchMethod">
            <el-button type="text" @click="switchLoginType">
              <span v-if="loginType === 'OTP'">Switch to Authenticator App (user MFA)</span>
              <span v-else>Switch to Email OTP (company MFA)</span>
            </el-button>
          </div>
        </el-form>
      </div>

      <div v-else class="text-center" style="margin-top: 2rem">
        <h3>Please wait...</h3>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { postAPICall } from '@/helpers/httpHelper';
  import Swal from 'sweetalert2';
  import WhiteLabelConfig from '../../config/app';
  import config from '../../config/app';
  import { clearDB } from '@/repo/appSettingRepo';

  export default {
    data() {
      return {
        defaultAvatar: 'https://cdn-icons-png.flaticon.com/512/847/847969.png',
        defaultLogo: require('../../assets/logo.webp'),
        otp: '',
        resendDisabled: false,
        resendCountdown: 0,
        verifying: false,
        submitDisabled: true,
        otpTimer: null,
        loginType: '',
        settings: {},
        mfaMethods: { OTP: false, APP: false },
        logoFetched: false,
      };
    },
    watch: {
      otp(newVal) {
        if (newVal.length === 6) {
          this.submitDisabled = false;
        } else {
          this.submitDisabled = true;
        }
      },
    },

    computed: {
      ...mapGetters('company', ['getContactLoginSlug', 'getSingleCompanyLogo']),
      ...mapGetters('auth', [
        'getErrorMessage',
        'getOtpVerifiedStatus',
        'getAuthenticatedUser',
        'getActiveWorkspace',
        'getActiveContactType',
      ]),
      ...mapGetters('entities', ['getEntityDataById', 'getEntityPrimaryData']),
      fullName() {
        return `${this.getAuthenticatedUser.first_name || ''} ${
          this.getAuthenticatedUser.last_name || ''
        }`.trim();
      },
      getLogo() {
        return this.getSingleCompanyLogo
          ? `${WhiteLabelConfig.S3_BUCKET_URL}/${this.getSingleCompanyLogo}`
          : '';
      },
      canSwitchMethod() {
        return this.mfaMethods.OTP && this.mfaMethods.APP;
      },
    },

    async created() {
      let id = this.getActiveContactType?.contact_type?._id;
      (await this.$store.dispatch('entities/fetchEntityById', {
        entity_id: id,
      }),
        await this.fetchMFASettings());
      this.setInitialLoginType();
    },

    methods: {
      logoutUser() {
        localStorage.removeItem('botId');
        localStorage.removeItem('token');
        localStorage.removeItem('internal_chat_enabled');

        window.bus.$emit('chatbot');
        Swal.fire({
          title: '',
          html: 'signing off....',
          timer: 3000,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
          didOpen: () => {
            Swal.showLoading();
            const preservedLoginStartTime = localStorage.getItem('loginStartTime');
            localStorage.removeItem(config.APP_NAME + '_' + config.APP_VERSION);
            localStorage.clear();
            if (preservedLoginStartTime) {
              localStorage.setItem('loginStartTime', preservedLoginStartTime);
            }
            this.resetStore();
            this.$store.dispatch('auth/reset');
            this.$store.commit('company/resetCompaniesByEmail');
            clearDB(true);
          },
          willClose: () => {
            this.$router.push('/signin/tenant');
          },
        });
        this.resetStore();
      },
      resetStore() {
        this.$store.commit('auth/resetAuthData');
        this.$store.commit('profile/resetProfileData');
        this.$store.commit('company/resetCompanyData');
        this.$store.commit('auth/resetAuthData', null, { root: true });
        this.$store.commit('company/resetCompanyData', null, { root: true });
        this.$store.commit('companyDocuments/resetCompanyDocumentsData', null, {
          root: true,
        });
        this.$store.commit('documentGroups/resetDocumentGroupsData', null, {
          root: true,
        });
        this.$store.commit('documentCategories/resetDocumentCategoriesData', null, {
          root: true,
        });

        this.$store.commit('companyBenefits/resetCompanyBenefitsData', null, {
          root: true,
        });
        this.$store.commit('companyTemplates/resetCompanyTemplatesData', null, {
          root: true,
        });
        this.$store.commit('offerLetter/resetOfferLetterData', null, {
          root: true,
        });
        this.$store.commit('employee/resetEmployeesData', null, {
          root: true,
        });
        this.$store.commit('configurableDocuments/resetConfigurableDocuments');

        this.$store.commit('standardDocumentConfig/reset');
        this.$store.commit('employeeData/reset');
        this.$store.commit('employeeDocuments/reset');
        this.$store.commit('workflowData/reset');
        this.$store.commit('workflows/reset');
        this.$store.commit('templatesData/reset');
        this.$store.commit('i9formDocuments/reset');
        this.$store.commit('authorizedRepresentatives/reset');
        this.$store.commit('employeeDocumentAuthorizedUsers/reset');
        this.$store.commit('entities/setEntityDataById', null, {
          root: true,
        });
      },
      async fetchMFASettings() {
        try {
          const companyId = this.getAuthenticatedUser?.company_id;
          const userMfa = false;

          await Promise.all([this.$store.dispatch('company/fetchCompanyLogo', companyId)]);

          this.mfaMethods = { OTP: false, APP: false }; // reset before setting

          if (this.getEntityDataById?.is_mfa) {
            this.mfaMethods.OTP = true;
            this.settings = this.getContactLoginSlug.mfa_settings;
          }

          if (userMfa) {
            this.mfaMethods.APP = true;
          }
        } catch (err) {
          console.error('Error fetching MFA settings:', err);
        } finally {
          this.logoFetched = true;
        }
      },

      setInitialLoginType() {
        if (this.mfaMethods.OTP) {
          this.loginType = 'OTP';
          this.handleSendOtp();
        } else if (this.mfaMethods.APP) {
          this.loginType = 'APP';
        } else {
          // No MFA enabled, redirect directly
          this.$router.push({
            path: '/ap/welcome',
            query: this.$route.query,
          });
        }
      },

      switchLoginType() {
        this.loginType = this.loginType === 'OTP' ? 'APP' : 'OTP';
        this.otp = '';
        this.submitDisabled = true;
        if (this.loginType === 'OTP') this.handleSendOtp();
      },

      async handleSendOtp() {
        try {
          this.otp = '';
          this.resendDisabled = true;
          this.resendCountdown = 60;
          this.submitDisabled = true;

          await postAPICall('POST', '/mfa-login/send-otp', {
            user_id: this.getAuthenticatedUser._id,
            workspace_id: this.getAuthenticatedUser?.company_id,
            login_type: 'OTP',
            email: this.getAuthenticatedUser.email,
            access_type: 'company_user',
          });

          Swal.fire({ icon: 'success', title: 'OTP sent to email', timer: 1500 });
          this.submitDisabled = false;

          this.otpTimer = setTimeout(() => {
            this.submitDisabled = true;
          }, 300000); // 5 minutes

          const resendTimer = setInterval(() => {
            this.resendCountdown--;
            if (this.resendCountdown <= 0) {
              clearInterval(resendTimer);
              this.resendDisabled = false;
            }
          }, 1000);
        } catch (err) {
          console.log('Otp err', err);
          Swal.fire({
            icon: 'error',
            title: err?.response?.data?.message || 'Failed to send OTP',
          });
          this.resendDisabled = false;
        }
      },

      async verifyOTP() {
        if (!this.otp || this.otp.length !== 6) {
          Swal.fire({ icon: 'warning', title: 'Enter a valid 6-digit code' });
          return;
        }

        if (this.loginType === 'OTP' && this.submitDisabled) {
          Swal.fire({ icon: 'error', title: 'OTP expired. Please resend.' });
          return;
        }

        this.verifying = true;

        try {
          const userId = this.getAuthenticatedUser._id;

          if (this.loginType === 'OTP') {
            const payload = {
              user_id: userId,
              workspace_id: this.getAuthenticatedUser?.company_id,
              otp: this.otp,
              login_type: 'OTP',
              email: this.getAuthenticatedUser.email,
              access_type: 'company_user',
            };

            const res = await postAPICall('POST', '/mfa-login/verify-otp', payload);
            if (res?.success) {
              Swal.fire({
                icon: 'success',
                title: 'Verification successful',
                timer: 1500,
              });

              this.$router.push({
                path: '/ap/welcome',
                query: this.$route.query,
              });
            } else {
              Swal.fire({ icon: 'error', title: res?.message || 'Invalid OTP' });
            }
          } else if (this.loginType === 'APP') {
            await this.$store.dispatch('auth/verify_MF2A_Otp', {
              token: this.otp,
              user_id: userId,
            });

            if (this.getOtpVerifiedStatus) {
              Swal.fire({ icon: 'success', title: 'OTP Verified', timer: 1500 });
              this.$router.push({
                path: '/ap/welcome',
                query: this.$route.query,
              });
            } else {
              Swal.fire({
                icon: 'error',
                title: this.getErrorMessage || 'Invalid code',
              });
            }
          }
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: err?.response?.data?.message || 'Verification failed',
          });
        } finally {
          this.verifying = false;
        }
      },

      onLogoError(e) {
        if (e?.target && e.target.src !== this.defaultLogo) {
          e.target.onerror = null;
          e.target.src = this.defaultLogo;
        }
      },
    },
  };
</script>

<style scoped>
  .otp-switch-wrapper {
    text-align: center;
    margin-top: 1rem;
  }
</style>

<style scoped>
  .welcome-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 3rem 1rem;
    min-height: 100vh;
    position: relative;
    overflow: hidden;
    z-index: 1;

    background-color: rgb(255 255 255 / 15%);
  }

  .welcome-page::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: -2;
    background-image:
      linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('../../assets/7714.jpg'),
      linear-gradient(160deg, #1e3c72, #2a5298);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    filter: brightness(1);
  }

  @keyframes panBackground {
    0% {
      background-position: 0% 0%;
    }

    100% {
      background-position: 100% 100%;
    }
  }

  .welcome-header {
    text-align: center;
    max-width: 600px;
    margin-bottom: 2rem;

    h1 {
      color: #2f3169;
    }
  }

  .welcome-headername {
    text-align: center;
    /* max-width: 600px; */

    h1 {
      color: #2f3169;
    }
  }

  .hero-img {
    width: 280px;
    margin-bottom: 1rem;
  }

  .setup-note {
    color: grey;
    font-weight: 600;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }

  .steps-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
    width: 100%;
    /* max-width: 500px; */
  }

  .full-card {
    align-items: center;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
    padding: 1rem 1.2rem;
    transition: background 0.3s ease;
    /* background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2); */

    background-color: rgb(255 255 255 / 99%);
    border: 1px solid rgb(30 2 2 / 20%);
    backdrop-filter: blur(6px);
    color: #fff;
    padding-left: 7%;
    padding-right: 7%;
  }

  .step-card {
    display: flex;
    align-items: center;
    border-radius: 12px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
    padding: 1rem 1.2rem;
    transition: background 0.3s ease;
    /* background-color: rgba(91, 61, 140, 0.7);
   */
    background: #00000008;
    border: 1px solid rgb(23 37 138 / 39%);
    backdrop-filter: blur(6px);
    color: #195d8e;
    box-shadow:
      inset 0 0 1px #fff3,
      0 1px 4px rgba(0, 0, 0, 0.2);
  }

  .icon-wrapper {
    width: 32px;
    height: 32px;
    margin-right: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loader {
    border: 3px solid #212931;
    border-top-color: transparent;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    animation: spin 1s linear infinite;
  }

  .step-done {
    font-size: 24px;
    color: #4caf50;
  }

  .step-description {
    font-size: 1.1rem;
    font-weight: 500;
    color: #101010;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .setup-wrapper {
    background: rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    padding: 2rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    max-width: 600px;
    margin: 0 auto;
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 2rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  /* .hero-img {
  width: 100px;
  height: auto;
} */

  .user-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .user-photo {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 2px solid #ffffffcc;
    object-fit: cover;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .otp-section {
    margin-top: 1.5rem;
    width: 100%;
    /* max-width: 400px; */
  }

  .otp-input-wrapper {
    display: flex;
    justify-content: center;
  }

  .otp-input {
    text-align: center;
    font-size: 1.2rem;
    letter-spacing: 4px;
    max-width: 250px;
  }

  .otp-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 0.5rem;
  }

  .setup-note {
    color: grey;
    font-weight: 600;
    font-size: 0.95rem;
    margin-top: 0.5rem;
    text-align: center;
  }

  .otp-input-wrapper {
    display: flex;
    justify-content: center;
  }

  .otp-submit-wrapper {
    text-align: center;
    margin-top: 1rem;
  }

  .otp-submit-btn {
    width: 66%;
    /* match OTP field width */
  }

  .otp-resend-wrapper {
    text-align: center;
    margin-top: 0.5rem;
  }

  .otp-resend-btn {
    font-size: 0.9rem;
    color: #2f54eb;
  }

  .otp-input-large {
    text-align: center;

    width: 400px;
    /* much wider input field */
  }

  .otp-submit-btn {
    width: 100%;
    width: 400px;
    /* same as input */
  }

  @media (max-width: 600px) {
    .otp-input-large {
      width: 100%;

      font-size: 1rem;
      /* slightly smaller text */
    }

    .otp-submit-btn {
      width: 70%;

      font-size: 1rem;
      /* slightly smaller text */
    }
  }

  .mfa-email {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  @media (max-width: 480px) {
    .setup-note {
      font-size: 0.95rem;
      line-height: 1.4;
      padding: 0 0.5rem;
    }
  }
</style>
